/**
 * ClaudeDocs — setup sections (overview → cli).
 *
 * Extracted verbatim from ../ClaudeDocs.tsx. No styling/logic changes.
 */

import React from 'react';

import { CodeBlock, Section } from './components';
import {
  MCP_ENDPOINT,
} from '../../../config/mcpConnect';
import { t } from '../../../i18n';

export const SetupSections: React.FC = () => (
  <>
    <Section id="overview" title="Overview">
      <p>
        {t('docs.overview.lead')} <code className="px-1 bg-gray-100 rounded">{MCP_ENDPOINT}</code>.{' '}
        {t('docs.overview.body')}
      </p>
      <p>
        Workspace isolation is enforced server-side on every tool call: a key for workspace A
        can never see workspace B&apos;s data, even if a tool argument is forged.
      </p>
    </Section>

    <Section id="quickstart" title="Quick start (60 seconds)">
      <ol className="list-decimal pl-5 space-y-2">
        <li>{t('docs.claudeCode.connect')}</li>
        <li>{t('docs.quickstart.signIn')}</li>
        <li>{t('docs.quickstart.ask')}</li>
      </ol>
    </Section>

    {/* Post-ratchet section: copy renders via t() — see enConnect.ts. */}
    <Section id="device" title={t('docs.device.title')}>
      <p>
        <strong>{t('docs.device.claudeCodeHeading')}</strong>
      </p>
      <p>{t('docs.claudeCode.connect')}</p>
      <p>
        <strong>{t('docs.device.webHeading')}</strong>
      </p>
      <p>{t('docs.device.webBody')}</p>
      <CodeBlock>{MCP_ENDPOINT}</CodeBlock>
      <p>
        <strong>{t('docs.device.ollamaHeading')}</strong>
      </p>
      <p>{t('docs.device.ollamaBody')}</p>
      <p>
        <strong>{t('docs.device.localHeading')}</strong>
      </p>
      <p>{t('docs.device.localBody')}</p>
      <p className="text-sm text-gray-600">{t('docs.device.keyNote')}</p>
    </Section>

    <Section id="oauth" title="OAuth 2.0 (recommended)">
      <p>
        <strong>OAuth is the recommended way</strong> to connect Claude or any third-party
        client. It supports scoped access (read / write / execute),
        rotating refresh tokens, and per-workspace consent — without ever copying a long-lived
        Bearer key. Bearer keys remain fully supported (see sections below) and are not
        deprecated.
      </p>
      <ol className="list-decimal pl-5 space-y-2">
        <li>
          Register a client once (Dynamic Client Registration, RFC 7591). Save{' '}
          <code className="px-1 bg-gray-100 rounded">client_id</code> and{' '}
          <code className="px-1 bg-gray-100 rounded">client_secret</code> — the secret is
          shown only once.
        </li>
        <li>
          Send the user to{' '}
          <code className="px-1 bg-gray-100 rounded">/oauth/authorize</code> with{' '}
          <code className="px-1 bg-gray-100 rounded">response_type=code</code>,{' '}
          <code className="px-1 bg-gray-100 rounded">client_id</code>,{' '}
          <code className="px-1 bg-gray-100 rounded">redirect_uri</code>,{' '}
          <code className="px-1 bg-gray-100 rounded">scope</code>, and{' '}
          <code className="px-1 bg-gray-100 rounded">state</code>. Public clients also send a{' '}
          <code className="px-1 bg-gray-100 rounded">code_challenge</code> (PKCE S256).
        </li>
        <li>
          The user approves on the BuzzBit X consent screen, picks a workspace, and is
          redirected back to your <code className="px-1 bg-gray-100 rounded">redirect_uri</code>{' '}
          with <code className="px-1 bg-gray-100 rounded">?code=…&amp;state=…</code>.
        </li>
        <li>
          POST the code to <code className="px-1 bg-gray-100 rounded">/oauth/token</code>{' '}
          with <code className="px-1 bg-gray-100 rounded">grant_type=authorization_code</code>.
          You receive an access token (1 hour) and a refresh token (30 days). Use the access
          token as a Bearer on every MCP request.
        </li>
        <li>
          When the access token expires, POST the refresh token to{' '}
          <code className="px-1 bg-gray-100 rounded">/oauth/token</code> with{' '}
          <code className="px-1 bg-gray-100 rounded">grant_type=refresh_token</code>. Refresh
          tokens rotate (OAuth 2.1); the response includes a new refresh token. Reusing an
          old refresh token revokes the entire token family.
        </li>
      </ol>
      <p>
        <strong>Register a client (DCR):</strong>
      </p>
      <CodeBlock>{`curl -X POST https://api.buzzbitx.com/oauth/register \\
  -H "Content-Type: application/json" \\
  -d '{
    "client_name": "My Integration",
    "redirect_uris": ["https://myapp.example/callback"],
    "scope": "read write"
  }'`}</CodeBlock>
      <p>
        <strong>Exchange code for token:</strong>
      </p>
      <CodeBlock>{`curl -X POST https://api.buzzbitx.com/oauth/token \\
  -u "<client_id>:<client_secret>" \\
  -d "grant_type=authorization_code" \\
  -d "code=<auth_code>" \\
  -d "redirect_uri=https://myapp.example/callback"`}</CodeBlock>
      <p>
        <strong>Use the access token on /mcp:</strong>
      </p>
      <CodeBlock>{`curl -X POST https://api.buzzbitx.com/mcp \\
  -H "Authorization: Bearer bbxat_<access_token>" \\
  -H "Accept: application/json, text/event-stream" \\
  -H "Content-Type: application/json" \\
  -d '{"jsonrpc":"2.0","id":1,"method":"tools/list"}'`}</CodeBlock>
      <p className="text-sm text-gray-600">
        Scopes: <code>read</code> (catalog + analytics reads), <code>write</code> (draft
        creation, segment management), <code>execute</code> (send campaigns, trigger flows).
        ADMIN is opt-in for billing + key management.
      </p>
    </Section>

    <Section id="plugin" title="Claude Code plugin">
      <p>{t('docs.claudeCode.connect')}</p>
    </Section>

    {/* A custom connector, not the config file: that file only runs local
        servers, so the URL-and-key snippet that used to be here never connected. */}
    <Section id="desktop" title="Claude Desktop">
      <p>{t('docs.desktop.steps')}</p>
      <CodeBlock>{MCP_ENDPOINT}</CodeBlock>
    </Section>

    <Section id="cli" title="bbx CLI">
      <p>
        For scripting and CI use. The CLI wraps every MCP tool as a subcommand and supports
        output formats <code className="px-1 bg-gray-100 rounded">--format=table|json|csv</code>.
      </p>
      <CodeBlock>{`npm install -g github:arad1do/buzzbit-cli
bbx auth login --key=bz_live_xxxxxxxxxxxxxxxx
bbx customers list --limit=20
bbx orders list --status=paid --format=csv > paid.csv`}</CodeBlock>
      <p>
        <strong>Multi-workspace (Agency):</strong>
      </p>
      <CodeBlock>{`bbx auth login --label=client-a --key=bz_live_aaa…
bbx auth login --label=client-b --key=bz_live_bbb…
bbx auth use client-a
bbx customers list           # runs against client-a`}</CodeBlock>
      <p>
        Full reference: 19 command groups, ~50 subcommands. See{' '}
        <a
          href="https://github.com/arad1do/buzzbit-cli#readme"
          target="_blank"
          rel="noopener noreferrer"
          className="text-gray-900 underline"
        >
          the CLI README
        </a>
        .
      </p>
    </Section>
  </>
);
